"use client";
import {
  AccountCircleOutlined,
  LocalActivityOutlined,
  RedeemOutlined,
  RoomServiceOutlined,
} from "@mui/icons-material";
import Link from "next/link";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { Page } from "~/types";
import { Box } from "../Box/Box";
import styles from "./NavigationBar.module.css";

const iconStyle = { fontSize: 32 };

export function NavigationBar({
  displayMenu,
  activeItem,
  locationRef,
  authenticated,
  userIsLoading,
}: {
  displayMenu: boolean;
  activeItem: Page | undefined;
  locationRef: string;
  authenticated: boolean;
  userIsLoading: boolean;
}): React.ReactElement {
  const accountLabel = authenticated ? "ACCOUNT" : "LOGIN";

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative"
      paddingX="5"
      paddingY="2"
      className={styles.navBar}
    >
      <Box display="flex" width="full" justifyContent="spaceBetween">
        {displayMenu ? (
          <NavLink
            active={activeItem === "menu"}
            icon="menu"
            label={"MENU"}
            href={`/${locationRef}/menu`}
          />
        ) : null}
        <NavLink
          active={activeItem === "loyalty"}
          icon="promotions"
          label="LOYALTY"
          href={`/${locationRef}/loyalty`}
        />

        <NavLink
          active={activeItem === "promo-code"}
          icon="promo-code"
          label="PROMO CODE"
          href={`/${locationRef}/account/promo-code`}
        />

        {userIsLoading && <NavLinkSkeleton icon="account" />}
        {!userIsLoading && (
          <NavLink
            active={["account", "activity"].includes(activeItem || "")}
            icon="account"
            label={accountLabel}
            href={
              authenticated
                ? `/${locationRef}/account`
                : "/login?next=" + encodeURIComponent(`/${locationRef}/menu`)
            }
          />
        )}
      </Box>
    </Box>
  );
}

function NavLink({
  active,
  icon,
  label,
  href,
}: {
  active: boolean;
  icon: string;
  label: string;
  href: string;
}): React.ReactElement {
  const [hover, setHover] = React.useState(false);

  let iconColor;
  if (active && hover) {
    iconColor = "var(--color-accent-highlight)";
  } else if (active || hover) {
    iconColor = "var(--color-accent)";
  } else {
    iconColor = "var(--color-jets-black)";
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      cursor={!active ? "pointer" : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Link
        href={href}
        className={active ? styles.navBarLinkActive : styles.navBarLink}
        prefetch={false}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginBottom="1"
          width="touchable"
        >
          <NavIcon icon={icon} color={iconColor} />
        </Box>
        <Box
          width="touchable"
          display="flex"
          justifyContent="center"
          style={{ whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      </Link>
    </Box>
  );
}

function NavIcon({
  icon,
  color,
}: {
  icon: string;
  color: string;
}): React.ReactElement {
  switch (icon) {
    case "menu":
      return <RoomServiceOutlined htmlColor={color} sx={iconStyle} />;
    case "promotions":
      return <RedeemOutlined htmlColor={color} sx={iconStyle} />;
    case "promo-code":
      return <LocalActivityOutlined htmlColor={color} sx={iconStyle} />;
    case "account":
      return <AccountCircleOutlined htmlColor={color} sx={iconStyle} />;
    default:
      return <></>;
  }
}

function NavLinkSkeleton({ icon }: { icon: string }) {
  return (
    <Box
      className={styles.navBarLink}
      display="flex"
      flexDirection="column"
      alignItems="center"
      cursor="not-allowed"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        marginBottom="1"
        width="touchable"
      >
        <NavIcon icon={icon} color="var(--color-jets-black)" />
      </Box>
      <Box
        width="touchable"
        display="flex"
        justifyContent="center"
        style={{ whiteSpace: "nowrap" }}
        flexGrow={1}
      >
        <Skeleton width={48} height={23} />
      </Box>
    </Box>
  );
}
